import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChatCircleDots,
  ClockCounterClockwise,
  FileText,
  Handshake,
  Target,
} from "@phosphor-icons/react";
import SeoPageShell from "@/components/SeoPageShell";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const source = "about";

const workingLoop = [
  {
    icon: ChatCircleDots,
    label: "Real conversations",
    title: "Built around the work that actually happens.",
    copy: "Renewals, difficult customer conversations, follow-ups, account context, internal handoffs, objections, and the pressure to remember what mattered before the next call.",
  },
  {
    icon: ClockCounterClockwise,
    label: "Power-user feedback loop",
    title: "Used every day, not only tested in demos.",
    copy: "I use Callsect myself in everyday account-management work. Friction in the real workflow becomes product feedback immediately instead of waiting for a hypothetical research cycle.",
  },
  {
    icon: Target,
    label: "Practical by design",
    title: "The test is whether it helps before or after the next customer conversation.",
    copy: "If a feature adds ceremony without improving understanding, follow-up, relationship memory, coaching, or preparation, it has not earned its place.",
  },
];

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function AboutPage() {
  return (
    <SeoPageShell source={source}>
      <main>
        <section className="seo-marketing-hero product-feature-hero about-page-hero px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28">
          <div className="product-feature-hero__glow" aria-hidden="true" />
          <div className="relative mx-auto max-w-7xl">
            <div className="max-w-4xl">
              <div className="landing-anti-summary-pill mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.2em] marketing-text-secondary">
                <span className="landing-anti-summary-icon" aria-hidden="true">
                  <ArrowRight size={14} weight="bold" />
                </span>
                <span>About Callsect</span>
              </div>
              <h1 className="product-feature-hero__title landing-hero-title max-w-4xl">
                <span className="product-feature-hero__title-base block">Built from real conversations.</span>
                <span className="product-feature-hero__title-accent landing-hero-spectrum block">Refined through real work.</span>
              </h1>
              <p className="product-feature-hero-subcopy landing-hero-subcopy marketing-text-secondary">
                Callsect is an independent product built and continuously refined alongside everyday account management—not around hypothetical customer-facing workflows.
              </p>
              <div className="product-feature-hero__actions">
                <Link
                  to="/login?mode=register"
                  onClick={() => track("hero", "sign_up_free")}
                  className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
                >
                  Sign up free
                </Link>
                <Link
                  to="/demo"
                  onClick={() => track("hero", "see_demo")}
                  className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition"
                >
                  See Demo
                  <ArrowRight size={17} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="product-feature-section marketing-band border-y px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="product-feature-section__intro">
              <span>Built from the work</span>
              <h2>The product and the power user are in the same feedback loop.</h2>
              <p>
                Callsect started from a simple frustration: customer conversations contain more useful context than a summary can carry. The product keeps evolving against the same real-world test—does it make the next customer interaction easier to understand, document, and prepare for?
              </p>
            </div>
            <div className="product-feature-grid product-feature-grid--three mt-12">
              {workingLoop.map(({ icon: Icon, label, title, copy }) => (
                <article className="product-feature-card" key={label}>
                  <Icon size={24} weight="duotone" aria-hidden="true" />
                  <span>{label}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="product-feature-section px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="product-feature-sticky">
              <span className="product-feature-kicker">Why that matters</span>
              <h2>New ideas start as friction in the actual workflow.</h2>
              <p>
                Callsect is not optimized around a checklist of category features. It is optimized around recurring moments where customer-facing work loses context or creates unnecessary effort.
              </p>
            </div>
            <div className="about-workflow-list">
              <article>
                <Handshake size={22} weight="duotone" aria-hidden="true" />
                <div>
                  <strong>Relationship context gets lost between calls.</strong>
                  <p>That becomes a reason to improve what Callsect remembers and how clearly it shows what changed.</p>
                </div>
              </article>
              <article>
                <FileText size={22} weight="duotone" aria-hidden="true" />
                <div>
                  <strong>Writing the follow-up becomes duplicate work.</strong>
                  <p>That becomes a reason to make CRM notes and customer-ready follow-ups useful without turning them into another editing task.</p>
                </div>
              </article>
              <article>
                <Target size={22} weight="duotone" aria-hidden="true" />
                <div>
                  <strong>The next call starts with reconstruction instead of preparation.</strong>
                  <p>That becomes a reason to carry coaching, objections, open loops, and the relationship state into a concrete next-call plan.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="product-feature-band px-5 pb-6 pt-2 sm:px-8 sm:pb-8">
          <div className="marketing-highlight-band mx-auto max-w-6xl rounded-3xl border p-7 sm:p-9">
            <div className="max-w-4xl">
              <ChatCircleDots size={25} weight="duotone" aria-hidden="true" />
              <h2>Born in account management. Built for customer-facing teams.</h2>
              <p>
                The testing ground is real account-management work, but the underlying problem is broader: Sales, Account Management, Customer Success, and Renewals all need the important parts of a customer conversation to survive into what happens next.
              </p>
            </div>
          </div>
        </section>

        <section className="product-feature-shell-final px-5 py-16 sm:px-8 sm:py-20">
          <div className="product-feature-final mx-auto max-w-5xl">
            <span>See it in practice</span>
            <h2>Finish the call. Carry the understanding forward.</h2>
            <p>Explore the real workflow with synthetic customer-call data, or sign up free and use Callsect with your own conversations.</p>
            <div>
              <Link
                to="/login?mode=register"
                onClick={() => track("final_cta", "sign_up_free")}
                className="product-feature-final__primary"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => track("final_cta", "see_demo")}
                className="product-feature-final__secondary"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
